import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import { AGENTS } from "../data/agents";
import { Printer, ShieldCheck, AlertTriangle } from "lucide-react";

export default function PublicReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/reports/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Link not available");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6" data-testid="public-report-error">
        <div className="bg-white border border-slate-200 rounded-xl p-10 max-w-md text-center">
          <AlertTriangle className="w-8 h-8 text-[#EF4444] mx-auto" />
          <p className="mt-4 font-display font-extrabold text-[#0B1B3D] text-xl">{error}</p>
          <p className="mt-2 text-sm text-[#475569]">Ask the sender for a fresh review link.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center bg-[#F8FAFC] text-[#94A3B8]">Loading…</div>;

  const r = data.report || {};
  const lastRound = (data.round_outputs || []).slice(-1)[0];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-report">
      <header className="border-b border-slate-200 bg-white no-print">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#0B1B3D] grid place-items-center"><span className="font-display font-black text-white text-sm">K</span></div>
            <span className="font-display font-extrabold text-[#0B1B3D]">Konzenzo</span>
            <span className="text-xs text-[#94A3B8] font-mono-kz tracking-widest ml-2 uppercase">Read-only review</span>
          </div>
          <button data-testid="public-print-btn" onClick={() => window.print()} className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-4 py-2 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2"><Printer className="w-4 h-4" /> Export PDF</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 sm:p-10">
        {data.shared_by?.name && (
          <div className="bg-white border border-[#0EA5E9]/30 rounded-xl p-5 mb-6 no-print" data-testid="public-shared-by">
            <p className="text-xs font-mono-kz text-[#0EA5E9] tracking-widest font-bold">SHARED FOR YOUR REVIEW</p>
            <p className="mt-2 text-sm text-[#475569]">
              <span className="font-bold text-[#0B1B3D]">{data.shared_by.name}</span>
              {data.shared_by.email && <> · <span className="font-mono-kz text-[#94A3B8]">{data.shared_by.email}</span></>}
            </p>
            {data.shared_note && <p className="mt-3 text-sm text-[#475569] border-l-2 border-[#0EA5E9] pl-3 italic">"{data.shared_note}"</p>}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-10 print-page">
          <div className="flex items-center justify-between border-b border-slate-200 pb-6 mb-8">
            <div>
              <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">KONZENZO DECISION REPORT</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black text-[#0B1B3D] mt-2 leading-tight">{data.title}</h1>
              <p className="text-sm text-[#475569] mt-2">{data.industry || "—"} · {data.country || "—"} · {data.company_size || "—"}</p>
            </div>
            <div className="text-right">
              <p className="font-mono-kz text-xs text-[#94A3B8]">CONFIDENCE</p>
              <p className="font-display font-black text-3xl text-[#10B981]">{Math.round(r.decision_confidence || 0)}%</p>
            </div>
          </div>

          <Section title="Executive summary"><p className="text-[#0B1B3D] leading-relaxed">{r.executive_summary || "—"}</p></Section>
          <Section title="Consensus recommendation"><div className="rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 p-5"><ShieldCheck className="w-5 h-5 text-[#10B981]" /><p className="mt-3 text-[#0B1B3D] leading-relaxed font-medium">{r.consensus_recommendation || "—"}</p></div></Section>
          <Section title="Business context"><p className="text-[#475569]">{data.description}</p>{data.desired_outcome && (<p className="mt-3 text-[#475569]"><span className="font-bold text-[#0B1B3D]">Desired outcome: </span>{data.desired_outcome}</p>)}</Section>
          <Section title="Experts involved">
            <div className="grid sm:grid-cols-2 gap-3">
              {data.agent_ids.map((aid) => { const a = AGENTS.find((x) => x.id === aid); return a ? (<div key={aid} className="border border-slate-200 rounded-lg p-3"><p className="font-display font-bold text-[#0B1B3D]">{a.name}</p><p className="text-xs font-mono-kz text-[#94A3B8] tracking-widest mt-0.5">{a.specialty}</p></div>) : null; })}
            </div>
          </Section>
          <Section title="Areas of agreement"><BulletList items={r.agreements} accent="text-[#10B981]" /></Section>
          <Section title="Areas of disagreement"><BulletList items={r.disagreements} accent="text-[#F59E0B]" /></Section>
          <Section title="Risks & mitigations">
            <div className="space-y-3">
              {(r.risks_and_mitigations || []).map((row) => (
                <div key={`${row.risk}-${row.mitigation}`} className="border border-slate-200 rounded-lg p-4 grid md:grid-cols-2 gap-4">
                  <div><p className="text-xs font-mono-kz text-[#EF4444] tracking-widest font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> RISK</p><p className="text-sm text-[#0B1B3D] mt-1">{row.risk}</p></div>
                  <div><p className="text-xs font-mono-kz text-[#10B981] tracking-widest font-bold">MITIGATION</p><p className="text-sm text-[#0B1B3D] mt-1">{row.mitigation}</p></div>
                </div>
              ))}
              {(!r.risks_and_mitigations || r.risks_and_mitigations.length === 0) && <p className="text-sm text-[#94A3B8]">—</p>}
            </div>
          </Section>
          <Section title="Action plan">
            <div className="space-y-2">
              {(r.action_plan || []).map((row, i) => (
                <div key={`${row.step}-${i}`} className="border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                  <span className="font-mono-kz text-xs font-bold text-[#0EA5E9] tracking-widest pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1"><p className="text-sm font-bold text-[#0B1B3D]">{row.step}</p><p className="text-xs text-[#94A3B8] font-mono-kz mt-1 tracking-widest">{row.owner || "—"} · {row.timeframe || "—"}</p></div>
                </div>
              ))}
              {(!r.action_plan || r.action_plan.length === 0) && <p className="text-sm text-[#94A3B8]">—</p>}
            </div>
          </Section>
          <Section title="Open questions"><BulletList items={r.open_questions} /></Section>
          <Section title="Final-round agent recommendations">
            <div className="space-y-2">
              {(lastRound?.agents || []).map((a) => (<div key={a.agent_id} className="border border-slate-200 rounded-lg p-3"><p className="font-display font-bold text-[#0B1B3D]">{a.agent_name}</p><p className="text-sm text-[#475569] mt-1">{a.recommendation}</p></div>))}
            </div>
          </Section>
          {r.suggested_next_review && <Section title="Suggested next expert review"><p className="text-[#475569]">{r.suggested_next_review}</p></Section>}
          <p className="mt-12 pt-6 border-t border-slate-200 text-xs text-[#94A3B8] font-mono-kz">Konzenzo provides structured AI analysis to support — not replace — qualified human review for tax, legal and financial matters.</p>
        </div>
      </main>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-display font-extrabold text-[#0B1B3D] text-xl mb-3">{title}</h2>
    {children}
  </section>
);

const BulletList = ({ items, accent = "text-[#0EA5E9]" }) => (
  <ul className="space-y-2">
    {(items || []).map((s) => (<li key={s} className="flex gap-2 text-[#475569]"><span className={`mt-1 ${accent}`}>•</span><span>{s}</span></li>))}
    {(!items || items.length === 0) && <p className="text-sm text-[#94A3B8]">—</p>}
  </ul>
);
